import React from 'react';
import { Navigate, Outlet, Routes, Route } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import LandingPage from './LandingPage';
import ParentShell, { STAGING_NOTE } from '../Layout/ParentShell';
import Home from '../../pages/Home/Home';
import Discover from '../../pages/Discover/Discover';
import MyStories from '../../pages/MyStories/MyStories';
import HouseholdStories from '../../pages/HouseholdStories/HouseholdStories';
import Voice from '../../pages/Voice/Voice';
import AuthCallback from '../../pages/AuthCallback/AuthCallback';
import RendersList from '../../pages/Renders/RendersList';
import RenderReview from '../../pages/Renders/RenderReview';
import StoryTime from '../../pages/StoryTime/StoryTime';
import Billing from '../../pages/Billing/Billing';
import AccountFamily from '../../pages/AccountFamily/AccountFamily';
import LibraryAdminQueue from '../../pages/LibraryAdmin/LibraryAdminQueue';
import LibraryEditor from '../../pages/LibraryAdmin/LibraryEditor';
import SharedStoryReview from '../../pages/LibraryAdmin/SharedStoryReview';
import CatalogGeneration from '../../pages/LibraryAdmin/CatalogGeneration';
import SoundbedAdmin from '../../pages/SoundbedAdmin/SoundbedAdmin';
import { Scene, Shell, StateMessage, PillButton } from '../ui';
import { BedtimeProvider } from '../../context/BedtimeContext';
import StoryTimeSetup from '../../pages/StoryTime/StoryTimeSetup';
import VoiceInvitations from '../../pages/Voice/VoiceInvitations';
import VoiceDetails from '../../pages/Voice/VoiceDetails';
import { features } from '../../config/features';

/** Signed-out surfaces: the night scene and a brand-only header. */
const SignedOutShell: React.FC = () => (
  <Scene>
    <Shell mode="donor" notice={features.stagingMode ? STAGING_NOTE : undefined}>
      <Outlet />
    </Shell>
  </Scene>
);

const AuthenticatedApp: React.FC = () => {
  const { state } = useApp();

  // Show loading status while checking authentication
  if (state.authLoading) {
    return (
      <Scene>
        <div className="auth-bootstrap">
          <StateMessage kind="loading" title="Opening Luma…" shape="text" headingLevel={1} />
        </div>
      </Scene>
    );
  }

  // Show landing page if user is not authenticated
  if (!state.isAuthenticated || !state.user) {
    return (
      <Routes>
        <Route element={<SignedOutShell />}>
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="*" element={<LandingPage />} />
        </Route>
      </Routes>
    );
  }

  // The previous bedtime route bundle was not retained when slices 1–3 were
  // consolidated. Keep the rollout switch meaningful and fail closed: parent
  // account/voice repair remains available, while handoff and child playback
  // cannot be entered until the complete bedtime bundle is enabled again.
  if (!features.bedtimeContextV1) {
    return (
      <Routes>
        <Route element={<ParentShell />}>
          <Route path="/voice" element={<Voice />} />
          <Route path="/voice/new" element={<Voice />} />
          <Route path="/voice/invitations" element={<VoiceInvitations />} />
          <Route path="/voice/:voiceId/personalize/:listenerId" element={<Voice />} />
          <Route path="/voice/:voiceId" element={<VoiceDetails />} />
          <Route path="/account" element={<AccountFamily />} />
          <Route path="/account/billing" element={<Billing />} />
          <Route path="/admin/library" element={<LibraryAdminQueue />} />
          <Route path="/admin/library/:itemKey" element={<LibraryEditor />} />
          <Route path="/admin/catalog-generation" element={<CatalogGeneration />} />
          <Route path="/admin/library/:itemKey/sharing" element={<SharedStoryReview />} />
          <Route path="/admin/soundbeds" element={<SoundbedAdmin />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="*" element={(
            <div className="auth-bootstrap" data-testid="bedtime-feature-disabled">
            <StateMessage
              kind="blocked"
              headingLevel={1}
              title="Bedtime is paused for an update"
              message="Story preparation and child playback are unavailable while a parent update is checked."
            />
            <div className="auth-bootstrap__actions">
              <PillButton variant="primary" to="/voice">Family voices</PillButton>
              <PillButton to="/account">Account &amp; family</PillButton>
            </div>
            </div>
          )} />
        </Route>
      </Routes>
    );
  }

  // Show full app if user is authenticated. Story time (child mode)
  // renders without the app shell: no header, no nav, nothing for a
  // small tap to wander into.
  return (
    <BedtimeProvider key={state.user.id}>
      <Routes>
        <Route path="/story-time" element={<StoryTime />} />
        <Route element={<ParentShell />}>
          <Route path="/" element={state.offlineSession ? <Navigate to="/my-stories" replace /> : <Home />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/my-stories" element={<MyStories />} />
          <Route path="/household-stories" element={<HouseholdStories />} />
          <Route path="/renders" element={<RendersList />} />
          <Route path="/renders/:renderId" element={<RenderReview />} />
          <Route path="/story-time/setup" element={<StoryTimeSetup />} />
          <Route path="/voice" element={<Voice />} />
          <Route path="/voice/new" element={<Voice />} />
          <Route path="/voice/invitations" element={<VoiceInvitations />} />
          <Route path="/voice/:voiceId/personalize/:listenerId" element={<Voice />} />
          <Route path="/voice/:voiceId" element={<VoiceDetails />} />
          <Route path="/account" element={<AccountFamily />} />
          <Route path="/account/billing" element={<Billing />} />
          <Route path="/admin/library" element={<LibraryAdminQueue />} />
          <Route path="/admin/library/:itemKey" element={<LibraryEditor />} />
          <Route path="/admin/catalog-generation" element={<CatalogGeneration />} />
          <Route path="/admin/library/:itemKey/sharing" element={<SharedStoryReview />} />
          <Route path="/admin/soundbeds" element={<SoundbedAdmin />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/stories" element={<Navigate to="/discover" replace />} />
          <Route path="/profile" element={<Navigate to="/account" replace />} />
        </Route>
      </Routes>
    </BedtimeProvider>
  );
};

export default AuthenticatedApp;
